import { useEffect } from "react";
import { SITE_URL, OG_IMAGE } from "@/content/seo/types";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const setMeta = (attr: string, key: string, content: string) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const SEOHead = ({ title, description, canonicalPath, ogType = "website", ogTitle, ogDescription, noindex, jsonLd }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);

    // Remove any pre-rendered static JSON-LD/canonical duplicates; this component owns them now.
    document.querySelectorAll("script[data-seo-jsonld-static]").forEach((el) => el.remove());

    const canonical = `${SITE_URL}${canonicalPath ?? ""}`;
    const links = document.querySelectorAll('link[rel="canonical"]');
    links.forEach((l, i) => i > 0 && l.remove());
    let link = links[0] as HTMLLinkElement | undefined;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    setMeta("property", "og:title", ogTitle ?? title);
    setMeta("property", "og:description", ogDescription ?? description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("name", "twitter:title", ogTitle ?? title);
    setMeta("name", "twitter:description", ogDescription ?? description);

    const robots = document.querySelector('meta[name="robots"]');
    if (noindex) setMeta("name", "robots", "noindex");
    else robots?.remove();

    let scriptEl = document.querySelector("script[data-seo-jsonld]") as HTMLScriptElement | null;
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.setAttribute("type", "application/ld+json");
        scriptEl.setAttribute("data-seo-jsonld", "true");
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      document.querySelector("script[data-seo-jsonld]")?.remove();
      if (noindex) document.querySelector('meta[name="robots"]')?.remove();
    };
  }, [title, description, canonicalPath, ogType, ogTitle, ogDescription, noindex, jsonLd]);

  return null;
};

export default SEOHead;
